import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { DashboardLayout } from "../../components/DashboardLayout";
import { links } from "./SuperAdminPages";
import api, { apiErrorMessage } from "../../lib/api";
import { toast } from "sonner";
import { ArrowLeft, Save } from "lucide-react";

export default function SuperAdminClinicCreatePage() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [form, setForm] = useState({
    name: "", slug: "", phone: "", address: "", email: "", website: "", description: "",
    departments: [],
    clinical_chart_mode: "general", dental_numbering_system: "fdi", default_dentition: "age_assisted",
    admin_name: "", admin_email: "", admin_password: "",
    plan_id: "",
  });
  const [busy, setBusy] = useState(false);
  const [departmentOptions, setDepartmentOptions] = useState([]);

  useEffect(() => {
    api.get("/plans").then((r) => setPlans(r.data || [])).catch(() => {});
    api.get("/public/directory/departments").then((r) => setDepartmentOptions(r.data.items || [])).catch(() => {});
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const generateTempPassword = () => {
    const arr = new Uint8Array(12);
    window.crypto.getRandomValues(arr);
    return "Mg" + Array.from(arr).map((b) => b.toString(36)).join("").slice(0, 14) + "!";
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.slug.trim() || !form.admin_name.trim() || !form.admin_email.trim()) {
      return toast.error("Fill required fields");
    }
    setBusy(true);
    try {
      const payload = { ...form, name: form.name.trim(), slug: form.slug.trim().toLowerCase(), admin_name: form.admin_name.trim(), admin_email: form.admin_email.trim().toLowerCase() };
      if (!payload.admin_password) payload.admin_password = generateTempPassword();
      if (!payload.plan_id) delete payload.plan_id;
      const r = await api.post("/admin/clinics", payload);
      toast.success(`Clinic created. Temp admin password: ${payload.admin_password}`, { duration: 10000 });
      navigate(`/super-admin/clinics/${r.data.clinic_id}`);
    } catch (err) {
      toast.error(apiErrorMessage(err, "Failed to create clinic"));
    }
    setBusy(false);
  };

  return (
    <DashboardLayout links={links} title="New clinic">
      <div className="flex items-center justify-between mb-4">
        <Link to="/super-admin/clinics" className="text-sm text-mg-medical font-semibold inline-flex items-center gap-1" data-testid="back-to-list">
          <ArrowLeft className="w-4 h-4" /> Back
        </Link>
      </div>
      <form onSubmit={submit} className="mg-card space-y-5 max-w-3xl">
        <div>
          <h3 className="font-bold text-mg-navy text-sm mb-3">Clinic details</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Clinic name *"><input value={form.name} onChange={(e) => set("name", e.target.value)} className="mg-input" required data-testid="new-clinic-name" /></Field>
            <Field label="Slug * (lowercase, no spaces)"><input value={form.slug} onChange={(e) => set("slug", e.target.value.toLowerCase())} className="mg-input" required data-testid="new-clinic-slug" /></Field>
            <Field label="Email"><input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="mg-input" /></Field>
            <Field label="Phone"><input value={form.phone} onChange={(e) => set("phone", e.target.value)} className="mg-input" /></Field>
            <Field label="Website"><input value={form.website} onChange={(e) => set("website", e.target.value)} className="mg-input" /></Field>
            <Field label="Plan">
              <select value={form.plan_id} onChange={(e) => set("plan_id", e.target.value)} className="mg-input" data-testid="new-clinic-plan">
                <option value="">Starter (default)</option>
                {plans.map((p) => <option key={p.plan_id} value={p.plan_id}>{p.name}</option>)}
              </select>
            </Field>
            <Field label="Address" full><input value={form.address} onChange={(e) => set("address", e.target.value)} className="mg-input" /></Field>
            <Field label="Description" full><textarea value={form.description} onChange={(e) => set("description", e.target.value)} className="mg-input min-h-[80px]" /></Field>
            <Field label="Departments (select all that apply)" full><div className="grid sm:grid-cols-2 md:grid-cols-3 gap-2 max-h-56 overflow-y-auto border rounded-xl p-3">{departmentOptions.map((department) => <label key={department} className="flex gap-2 items-center text-xs"><input type="checkbox" checked={form.departments.includes(department)} onChange={(e) => set("departments", e.target.checked ? [...form.departments,department] : form.departments.filter((value) => value !== department))} />{department}</label>)}</div></Field>
          </div>
        </div>

        <div className="border-t border-slate-100 pt-4">
          <h3 className="font-bold text-mg-navy text-sm mb-3">Clinical chart</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Field label="Clinic workflow"><select className="mg-input" value={form.clinical_chart_mode} onChange={(e) => set("clinical_chart_mode", e.target.value)} data-testid="new-clinical-chart-mode"><option value="general">General medical</option><option value="dental">Dental</option><option value="both">Both</option></select></Field>
            <Field label="Tooth numbering"><select className="mg-input" value={form.dental_numbering_system} onChange={(e) => set("dental_numbering_system", e.target.value)} disabled={form.clinical_chart_mode === "general"}><option value="fdi">FDI / ISO</option><option value="universal">Universal</option><option value="palmer">Palmer</option></select></Field>
            <Field label="Default dentition"><select className="mg-input" value={form.default_dentition} onChange={(e) => set("default_dentition", e.target.value)} disabled={form.clinical_chart_mode === "general"}><option value="age_assisted">Age assisted</option><option value="permanent">Permanent</option><option value="primary">Primary</option></select></Field>
          </div>
        </div>

        <div className="border-t border-slate-100 pt-4">
          <h3 className="font-bold text-mg-navy text-sm mb-3">Primary admin</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Admin name *"><input value={form.admin_name} onChange={(e) => set("admin_name", e.target.value)} className="mg-input" required data-testid="new-admin-name" /></Field>
            <Field label="Admin email *"><input type="email" value={form.admin_email} onChange={(e) => set("admin_email", e.target.value.toLowerCase())} className="mg-input" required data-testid="new-admin-email" /></Field>
            <Field label="Temp password (auto-generated if blank)" full>
              <input value={form.admin_password} onChange={(e) => set("admin_password", e.target.value)} className="mg-input font-mono" placeholder="Leave blank for auto-generated" data-testid="new-admin-password" />
            </Field>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
          <Link to="/super-admin/clinics" className="mg-btn-secondary text-sm">Cancel</Link>
          <button type="submit" disabled={busy} className="mg-btn-primary text-sm" data-testid="submit-new-clinic">
            <Save className="w-4 h-4" /> {busy ? "Creating…" : "Create clinic"}
          </button>
        </div>
      </form>
    </DashboardLayout>
  );
}

function Field({ label, children, full }) {
  return (
    <label className={`block ${full ? "md:col-span-2" : ""}`}>
      <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
